import "./_AnimeSeriesPanel.scss";
import "./_AnimeSeriesPanelMobile.scss";
import { useEffect, useRef, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";

function _AnimeSeriesPanel() {
  const [seriesName, setSeriesName] = useState("");
  const [seriesLogo, setSeriesLogo] = useState(""); // preview or existing path
  const [seriesData, setSeriesData] = useState([]);
  const [checked, setChecked] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const safeName = (name) => {
    if (!name) return "";
    // Replace illegal filename characters and trim whitespace
    //console.log(name.replace(/[\/\\:*?"<>|]/g, "_").trim());
    return name.replace(/[\/\\:*?"<>|]/g, "_").trim();
  };
  // 🟢 Fetch Anime Series
  const fetchAnimeSeries = () => {
    fetch(`${process.env.REACT_APP_BASE_URL}api/product/get-anime-series`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        setSeriesData(data.animeSeries || []);
        setChecked(new Array(data.animeSeries.length).fill(false));
      })
      .catch((err) => console.error("Error fetching anime series:", err));
  };

  useEffect(() => {
    fetchAnimeSeries();
  }, []);

  // 🟠 Handle File Selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setSeriesLogo(URL.createObjectURL(file)); // temporary preview
    }
  };

  const handleSelectImage = () => fileInputRef.current.click();

  // 🟡 Save or Update Anime Series
  const handleSave = () => {
    if (!seriesName.trim()) {
      alert("Anime series name cannot be empty.");
      return;
    }

    const formData = new FormData();
    formData.append("seriesName", seriesName);
    if (selectedFile) formData.append("seriesLogo", selectedFile);
    if (isEditing) formData.append("editingId", editingId);

    const url = isEditing
      ? `https://littlekidinside.shop/api/product/update-anime-series/${editingId}`
      : `https://littlekidinside.shop/api/product/add-anime-series`;

    fetch(url, {
      method: isEditing ? "PUT" : "POST",
      body: formData,
    })
      .then((res) => res.json())
      .then(() => {
        alert(isEditing ? "Anime series updated!" : "Anime series saved!");
        handleCancel();
        fetchAnimeSeries();
      })
      .catch((err) => console.error("Error saving anime series:", err));
  };

  // 🧹 Reset form
  const handleCancel = () => {
    setSeriesName("");
    setSelectedFile(null);
    setSeriesLogo("");
    setIsEditing(false);
    setEditingId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // 🗑️ Delete Single Series
  const handleDelete = (item) => {
    if (item.item_count > 0) {
      alert(
        `❌ Cannot delete "${item.AS_Name}" because it still has ${item.item_count} item(s).`
      );
      return;
    }

    if (!window.confirm(`Delete "${item.AS_Name}"?`)) return;

    fetch(
      `${process.env.REACT_APP_BASE_URL}api/product/delete-anime-series/${item.AS_ID}`,
      {
        method: "DELETE",
      }
    )
      .then((res) => res.json())
      .then(() => {
        alert("Anime series deleted!");
        fetchAnimeSeries();
      })
      .catch((err) => console.error("Error deleting anime series:", err));
  };

  // 🗑️ Delete multiple selected
  const handleDeleteSelected = () => {
    const selectedItems = seriesData.filter((_, i) => checked[i]);
    const deletable = selectedItems.filter((i) => i.item_count === 0);
    const blocked = selectedItems.filter((i) => i.item_count > 0);

    if (selectedItems.length === 0)
      return alert("Please select at least one anime series.");

    if (blocked.length > 0) {
      alert(
        `❌ These cannot be deleted because they have items:\n${blocked
          .map((b) => b.AS_Name)
          .join(", ")}`
      );
      return;
    }

    if (!window.confirm(`Delete ${deletable.length} selected anime series?`))
      return;

    Promise.all(
      deletable.map((i) =>
        fetch(
          `${process.env.REACT_APP_BASE_URL}api/product/delete-anime-series/${i.AS_ID}`,
          { method: "DELETE" }
        )
      )
    )
      .then(() => {
        alert(`✅ Deleted ${deletable.length} anime series!`);
        fetchAnimeSeries();
      })
      .catch((err) => console.error("Error deleting selected:", err));
  };

  // ✏️ Edit Anime Series
  const handleEdit = (item) => {
    setSeriesName(item.AS_Name);
    setSeriesLogo(
      item.AS_Logopath
        ? `${process.env.REACT_APP_BASE_URL}Assets/AnimeSeries/${safeName(
            item.AS_Name
          )}/${item.AS_Logopath}`
        : ""
    );
    setIsEditing(true);
    setEditingId(item.AS_ID);
    setSelectedFile(null);
  };

  // ☑️ Checkbox handling
  const handleCheck = (index) => {
    const updated = [...checked];
    updated[index] = !updated[index];
    setChecked(updated);
  };

  const handleCheckAll = (e) => {
    const all = e.target.checked;
    setChecked(new Array(seriesData.length).fill(all));
  };

  const allChecked = checked.length > 0 && checked.every(Boolean);

  // 🔍 Search filter
  const filteredSeries = seriesData.filter((item) =>
    (item.AS_Name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <main className="ad-anime-container">
      <div className="anime-panel-header">
        <div className="anime-panel-title-wrapper">
          <span>ANIME SERIES PANEL</span>
        </div>
      </div>

      <div className="anime-panel-container">
        <div className="anime-list">
          <div className="actions">
            {checked.some((v) => v) && (
              <button
                className="delete-selected-btn"
                onClick={handleDeleteSelected}
              >
                DELETE
              </button>
            )}
            <input
              type="text"
              placeholder="Keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="anime-panel-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>NO.</th>
                  <th>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={handleCheckAll}
                    />
                  </th>
                  <th>Logo</th>
                  <th>Anime Series Name</th>
                  <th>Items</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredSeries.length > 0 ? (
                  filteredSeries.map((item, index) => (
                    <tr
                      key={item.AS_ID}
                      className={editingId === item.AS_ID ? "editing" : ""}
                    >
                      <td>{index + 1}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={checked[index] || false}
                          onChange={() => handleCheck(index)}
                        />
                      </td>
                      <td>
                        {item.AS_Logopath ? (
                          <img
                            src={`${
                              process.env.REACT_APP_BASE_URL
                            }Assets/AnimeSeries/${safeName(item.AS_Name)}/${
                              item.AS_Logopath
                            }`}
                            alt="logo"
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "8px",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{item.AS_Name}</td>
                      <td>{item.item_count}</td>
                      <td>
                        <div className="td-action-btns-wrapper">
                          <FaEdit
                            className="action-icon edit"
                            onClick={() => handleEdit(item)}
                          />
                          <FaTrash
                            className="action-icon delete"
                            onClick={() => handleDelete(item)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center" }}>
                      No anime series found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="add-anime">
          <div className="anime-input-wrapper">
            <label>
              {isEditing ? "Edit Anime Series:" : "Add Anime Series:"}
            </label>
            <input
              type="text"
              value={seriesName}
              onChange={(e) => setSeriesName(e.target.value)}
            />
          </div>

          <div className="anime-input-wrapper">
            <button onClick={handleSelectImage}>
              {isEditing ? "Change Image" : "Upload Image"}
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".jpg,.jpeg,.png"
              onChange={handleFileChange}
            />
            <p>FILE EXTENSION: .JPG, .JPEG, .PNG</p>
            {seriesLogo && (
              <img src={seriesLogo} alt="Preview" className="logo-preview" />
            )}
          </div>

          <div className="anime-btn-action-wrapper">
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
            <button className="save-btn" onClick={handleSave}>
              {isEditing ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}

export default _AnimeSeriesPanel;
